import { ApiService } from "services/serverInClient";
import { PageContainer } from "components";
import { Formatter } from "utils/formatter";
import { useEffect, useState, useRef, useMemo } from "react";
import { useRouter } from "next/router";
import { useForm, FormProvider } from "react-hook-form";
import { toast } from "react-toastify";
import Toast from "components/Toast/Toast";
import { useLocale } from "hooks";
import translation from "./translation";
import { NewsListComponent } from "./components";
import { ITEMS_PER_PAGE } from "./components/constants";
import { concatQueryStringToUrl } from "utils/newFunction";
import type { ReactPaginateProps } from "react-paginate";
import type { GetContentParams } from "types";

function NewsList({
  data,
  tabs,
  mappedTabs,
  mappedTagTrees,
  breadcrumb,
  contentType,
}) {
  const methods = useForm({ defaultValues: data?.defaultValues });
  const { locale } = useLocale();
  const router = useRouter();
  const [activeTab, setActiveTab] = useState<string>(
    () => router.query.tab || tabs[0]
  ); // news | events | gathering | speechs
  const [loading, setLoading] = useState<boolean>(false);
  const [listData, setListData] = useState<Array<any>>(
    () => data?.cmsData || []
  );
  const [currentPage, setCurrentPage] = useState<number>(() => data?.page || 1);
  const [totalPagesCount, setTotalPagesCount] = useState<number>(() =>
    data?.cmsCount ? Math.ceil(data?.cmsCount / ITEMS_PER_PAGE) : 0
  );
  const firstTimeRef = useRef<boolean>(true);

  const noIndex = useMemo(() => {
    const QueryKeys = Object.keys(router.query);
    return QueryKeys?.filter((item) => item !== "tab")?.length > 0;
  }, [router.query, activeTab, tabs]);

  useEffect(() => {
    if (!firstTimeRef.current) {
      (async () => {
        const apiService = new ApiService(router.locale);
        let { search, category } = router.query;

        // methods.reset({
        //   search: search || "",
        //   category: category || "",
        // });
        const page = Number.isNaN(router.query?.page)
          ? 1
          : +(router.query.page || 1);

        const tabFromUrl = (router?.query?.tab as string) || tabs[0];
        setActiveTab(tabFromUrl);

        const objToSend: GetContentParams = {
          tagTreesCode: [],
          tagTrees: [],
          contentTypeUniqueId: contentType,
          size: ITEMS_PER_PAGE,
          offset: (page - 1) * ITEMS_PER_PAGE,
          fieldCode: [],
          fieldValue: [],
          orderBy: ["content.publish_date:desc"],
          orderTagTree: "child",
        };
        if (search) {
          objToSend.fieldCode.push("title");
          objToSend.fieldValue.push(search);
        }

        if (category) {
          objToSend.tagTrees.push(category?.value || category);
        } else {
          objToSend.tagTrees.push(mappedTagTrees[locale][tabFromUrl || "news"]);
        }
        try {
          setLoading(true);
          const res = await apiService.getContent(objToSend, {
            formatter: false,
          });

          setListData(Formatter.formatData({ data: res }));
          setCurrentPage(page);
          setTotalPagesCount(Math.ceil((res?.count || 0) / ITEMS_PER_PAGE));
        } catch (error) {
          console.log(error);
          toast.error(translation[locale]?.FAILED);
        } finally {
          setLoading(false);
        }
      })();
    } else {
      firstTimeRef.current = false;
    }
  }, [router.query]);

  useEffect(() => {
    if (
      methods.watch("category") &&
      !firstTimeRef.current &&
      methods.watch("category")?.value !== router.query.category
    ) {
      const queries = {
        ...router.query,
        category: methods.watch("category")?.value || "",
      };

      delete queries.page;

      if (queries.tab === tabs[0]) {
        delete queries.tab;
      }
      router.push(concatQueryStringToUrl(router.basePath, queries), undefined, {
        shallow: true,
      });
    }
  }, [methods.watch("category")]);

  const handleSearch = () => {
    const { search, category } = methods.getValues();
    const queries = {
      ...router.query,
      search: search || "",
      category: category?.value || "",
    };

    delete queries.page;

    if (queries.tab === tabs[0]) {
      delete queries.tab;
    }

    router.push(concatQueryStringToUrl(router.basePath, queries), undefined, {
      shallow: true,
    });
  };

  const goTab = (tabKey) => {
    const queries = {
      ...router.query,
      search: "",
      category: "",
      tab: tabKey,
    };

    delete queries.page;

    if (queries.tab === tabs[0]) {
      delete queries.tab;
    }

    methods.reset({ category: "", search: "" });
    router.push(concatQueryStringToUrl(router.basePath, queries), undefined, {
      shallow: true,
    });
  };

  const onPageChange: ReactPaginateProps["onPageChange"] = ({ selected }) => {
    let queries = {
      ...router.query,
      page: selected + 1,
      search: methods.watch("search") || "",
      category: methods.watch("category")?.value || "",
    };

    if (queries.page === 1) {
      delete queries.page;
    }

    if (queries.tab === tabs[0]) {
      delete queries.tab;
    }

    router.push(concatQueryStringToUrl(router.basePath, queries), undefined, {
      shallow: true,
    });
  };
  const activeMetaTag =
    activeTab === tabs[0] ? data?.newsMetaTag : data?.eventsMetaTag;
  return (
    <PageContainer
      breadCrumb={breadcrumb?.[locale]}
      metaTag={activeMetaTag}
      changeLanguageLink={activeMetaTag?.changeLanguageLink}
      noIndex={noIndex}
    >
      <Toast />
      <FormProvider {...methods}>
        <NewsListComponent
          data={data}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          totalPagesCount={totalPagesCount}
          setTotalPagesCount={setTotalPagesCount}
          onPageChange={onPageChange}
          // handleSearch={handleSearch}
          goTab={goTab}
          handleSearch={handleSearch}
          loading={loading}
          currentPage={currentPage}
          listData={listData}
          mappedTabs={mappedTabs}
          tabs={tabs}
        />
      </FormProvider>
    </PageContainer>
  );
}

export default NewsList;
